import React, { useState, useEffect } from 'react';
import './ModulePermissionSelector.css';

export interface ModulePermission {
  moduleName: string;
  displayName: string;
  canCreate: boolean;
  canRead: boolean;
  canUpdate: boolean;
  canDelete: boolean;
}

interface ModulePermissionSelectorProps {
  selectedPermissions: ModulePermission[];
  onChange: (permissions: ModulePermission[]) => void;
  disabled?: boolean;
}

const SYSTEM_MODULES = [
  { name: 'schedules', displayName: 'Schedules' },
  { name: 'students', displayName: 'Students' },
  { name: 'therapists', displayName: 'Therapists' },
  { name: 'fees', displayName: 'Fees Management' },
  { name: 'payments', displayName: 'Payments' },
  { name: 'invoices', displayName: 'Invoice' },
  { name: 'accounting', displayName: 'Accounting' },
  { name: 'document-transmittals', displayName: 'Document Transmittals' },
  { name: 'pending-reports', displayName: 'Pending Reports' },
  { name: 'settings', displayName: 'Settings' },
  { name: 'users', displayName: 'User Management' },
  { name: 'roles', displayName: 'Role Management' }
];

const ModulePermissionSelector: React.FC<ModulePermissionSelectorProps> = ({
  selectedPermissions,
  onChange,
  disabled = false
}) => {
  const [permissions, setPermissions] = useState<ModulePermission[]>([]);

  useEffect(() => {
    // Initialize permissions with existing ones or empty state
    const initialPermissions = SYSTEM_MODULES.map(module => {
      const existing = selectedPermissions.find(p => p.moduleName === module.name);
      return existing || {
        moduleName: module.name,
        displayName: module.displayName,
        canCreate: false,
        canRead: false,
        canUpdate: false,
        canDelete: false
      };
    });
    setPermissions(initialPermissions);
  }, [selectedPermissions]);

  const handleModuleToggle = (moduleName: string, checked: boolean) => {
    const updatedPermissions = permissions.map(permission => {
      if (permission.moduleName === moduleName) {
        if (checked) {
          // When enabling a module, give read permission by default
          return {
            ...permission,
            canRead: true,
            canCreate: true,
            canUpdate: true,
            canDelete: false // Delete is more sensitive, don't enable by default
          };
        } else {
          // When disabling a module, remove all permissions
          return {
            ...permission,
            canCreate: false,
            canRead: false,
            canUpdate: false,
            canDelete: false
          };
        }
      }
      return permission;
    });

    setPermissions(updatedPermissions);
    
    // Only pass back permissions for modules that have at least one permission enabled
    const activePermissions = updatedPermissions.filter(p => 
      p.canCreate || p.canRead || p.canUpdate || p.canDelete
    );
    onChange(activePermissions);
  };

  const handlePermissionChange = (moduleName: string, permissionType: keyof Omit<ModulePermission, 'moduleName' | 'displayName'>, checked: boolean) => {
    const updatedPermissions = permissions.map(permission => {
      if (permission.moduleName === moduleName) {
        const updated = { ...permission, [permissionType]: checked };
        
        // If unchecking read permission, uncheck all others
        if (permissionType === 'canRead' && !checked) {
          updated.canCreate = false;
          updated.canUpdate = false;
          updated.canDelete = false;
        }
        
        // If checking any permission, ensure read is also checked
        if (permissionType !== 'canRead' && checked) {
          updated.canRead = true;
        }
        
        return updated;
      }
      return permission;
    });

    setPermissions(updatedPermissions);
    
    // Only pass back permissions for modules that have at least one permission enabled
    const activePermissions = updatedPermissions.filter(p => 
      p.canCreate || p.canRead || p.canUpdate || p.canDelete
    );
    onChange(activePermissions);
  };

  const isModuleEnabled = (permission: ModulePermission) => {
    return permission.canCreate || permission.canRead || permission.canUpdate || permission.canDelete;
  };

  return (
    <div className="module-permission-selector">
      <label className="form-label">Module Permissions</label>
      <div className="modules-container">
        {permissions.map((permission) => {
          const moduleEnabled = isModuleEnabled(permission);
          
          return (
            <div key={permission.moduleName} className="module-item">
              <div className="module-header">
                <label className="module-checkbox">
                  <input
                    type="checkbox"
                    checked={moduleEnabled}
                    onChange={(e) => handleModuleToggle(permission.moduleName, e.target.checked)}
                    disabled={disabled}
                  />
                  <span className="module-name">{permission.displayName}</span>
                </label>
              </div>
              
              {moduleEnabled && (
                <div className="permission-details">
                  <div className="permission-grid">
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={permission.canRead}
                        onChange={(e) => handlePermissionChange(permission.moduleName, 'canRead', e.target.checked)}
                        disabled={disabled}
                      />
                      <span>View</span>
                    </label>
                    
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={permission.canCreate}
                        onChange={(e) => handlePermissionChange(permission.moduleName, 'canCreate', e.target.checked)}
                        disabled={disabled}
                      />
                      <span>Create</span>
                    </label>
                    
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={permission.canUpdate}
                        onChange={(e) => handlePermissionChange(permission.moduleName, 'canUpdate', e.target.checked)}
                        disabled={disabled}
                      />
                      <span>Edit</span>
                    </label>
                    
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={permission.canDelete}
                        onChange={(e) => handlePermissionChange(permission.moduleName, 'canDelete', e.target.checked)}
                        disabled={disabled}
                      />
                      <span>Delete</span>
                    </label>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="permission-note">
        <small>Select the modules this user can access. For each module, you can specify what actions they can perform.</small>
      </div>
    </div>
  );
};

export default ModulePermissionSelector;